import {
  ArrowRight,
  Briefcase,
  CalendarHeart,
  CheckCircle,
  Gift,
  Handshake,
  Heart,
  Mail,
  MapPin,
  Megaphone,
  Phone,
  Star,
  Target,
  UserCheck,
  Users,
} from "lucide-react";
import { useTranslation } from "react-i18next";

export default function GetInvolve() {
  const { t } = useTranslation();

  const involvementOptions = [
    {
      title: t("getInvolved.options.volunteer.title"),
      description: t("getInvolved.options.volunteer.description"),
      features: [
        t("getInvolved.options.volunteer.features.flexible"),
        t("getInvolved.options.volunteer.features.training"),
        t("getInvolved.options.volunteer.features.mentorship"),
        t("getInvolved.options.volunteer.features.impact"),
      ],
      buttonText: t("getInvolved.options.volunteer.button"),
      buttonLink: "/volunteer",
      icon: <UserCheck className="h-8 w-8" />,
      color: "from-green-600 to-green-700",
      bgColor: "bg-green-100",
      iconColor: "text-green-700",
    },
    {
      title: t("getInvolved.options.donate.title"),
      description: t("getInvolved.options.donate.description"),
      features: [
        t("getInvolved.options.donate.features.taxDeductible"),
        t("getInvolved.options.donate.features.transparent"),
        t("getInvolved.options.donate.features.updates"),
        t("getInvolved.options.donate.features.secure"),
      ],
      buttonText: t("getInvolved.options.donate.button"),
      buttonLink: "/donate",
      icon: <Gift className="h-8 w-8" />,
      color: "from-yellow-500 to-yellow-600",
      bgColor: "bg-yellow-100",
      iconColor: "text-yellow-700",
    },
    {
      title: t("getInvolved.options.partner.title"),
      description: t("getInvolved.options.partner.description"),
      features: [
        t("getInvolved.options.partner.features.longTerm"),
        t("getInvolved.options.partner.features.joint"),
        t("getInvolved.options.partner.features.csr"),
        t("getInvolved.options.partner.features.impact"),
      ],
      buttonText: t("getInvolved.options.partner.button"),
      buttonLink: "/#contact",
      icon: <Handshake className="h-8 w-8" />,
      color: "from-blue-600 to-blue-700",
      bgColor: "bg-blue-100",
      iconColor: "text-blue-700",
    },
    {
      title: t("getInvolved.options.advocate.title"),
      description: t("getInvolved.options.advocate.description"),
      features: [
        t("getInvolved.options.advocate.features.social"),
        t("getInvolved.options.advocate.features.events"),
        t("getInvolved.options.advocate.features.resources"),
        t("getInvolved.options.advocate.features.network"),
      ],
      buttonText: t("getInvolved.options.advocate.button"),
      buttonLink: "/advocate",
      icon: <Megaphone className="h-8 w-8" />,
      color: "from-purple-600 to-purple-700",
      bgColor: "bg-purple-100",
      iconColor: "text-purple-700",
    },
    {
      title: t("getInvolved.options.internships.title"),
      description: t("getInvolved.options.internships.description"),
      features: [
        t("getInvolved.options.internships.features.projects"),
        t("getInvolved.options.internships.features.skills"),
        t("getInvolved.options.internships.features.mentorship"),
        t("getInvolved.options.internships.features.growth"),
      ],
      buttonText: t("getInvolved.options.internships.button"),
      buttonLink: "/internships",
      icon: <Briefcase className="h-8 w-8" />,
      color: "from-indigo-600 to-indigo-700",
      bgColor: "bg-indigo-100",
      iconColor: "text-indigo-700",
    },
    {
      title: t("getInvolved.options.fundraiser.title"),
      description: t("getInvolved.options.fundraiser.description"),
      features: [
        t("getInvolved.options.fundraiser.features.planning"),
        t("getInvolved.options.fundraiser.features.engagement"),
        t("getInvolved.options.fundraiser.features.tools"),
        t("getInvolved.options.fundraiser.features.tracking"),
      ],
      buttonText: t("getInvolved.options.fundraiser.button"),
      buttonLink: "/fundraiser",
      icon: <CalendarHeart className="h-8 w-8" />,
      color: "from-pink-600 to-pink-700",
      bgColor: "bg-pink-100",
      iconColor: "text-pink-700",
    },
  ];

  const impactStats = [
    {
      number: "500+",
      label: t("getInvolved.stats.volunteers"),
      icon: <Users className="h-6 w-6" />,
    },
    {
      number: "50+",
      label: t("getInvolved.stats.communities"),
      icon: <Target className="h-6 w-6" />,
    },
    {
      number: "10,000+",
      label: t("getInvolved.stats.lives"),
      icon: <Heart className="h-6 w-6" />,
    },
    {
      number: "95%",
      label: t("getInvolved.stats.satisfaction"),
      icon: <Star className="h-6 w-6" />,
    },
  ];

  const faqs = [
    {
      question: t("getInvolved.faqs.experience.question"),
      answer: t("getInvolved.faqs.experience.answer"),
    },
    {
      question: t("getInvolved.faqs.donation.question"),
      answer: t("getInvolved.faqs.donation.answer"),
    },
    {
      question: t("getInvolved.faqs.partnership.question"),
      answer: t("getInvolved.faqs.partnership.answer"),
    },
    {
      question: t("getInvolved.faqs.advocate.question"),
      answer: t("getInvolved.faqs.advocate.answer"),
    },
    {
      question: t("getInvolved.faqs.fundraiser.question"),
      answer: t("getInvolved.faqs.fundraiser.answer"),
    },
    {
      question: t("getInvolved.faqs.internships.question"),
      answer: t("getInvolved.faqs.internships.answer"),
    },
  ];

  const volunteerDonorStories = [
    {
      name: "Arop Deng",
      role: t("getInvolved.stories.volunteer"),
      story: t("getInvolved.stories.story1"),
      location: "Juba, South Sudan",
      avatar: "/images/volunteer-1.jpg",
    },
    {
      name: "Nyaluak Chol",
      role: t("getInvolved.stories.donor"),
      story: t("getInvolved.stories.story2"),
      location: "Wau, South Sudan",
      avatar: "/images/donor-1.jpg",
    },
    {
      name: "Khamis Abuk",
      role: t("getInvolved.stories.volunteer"),
      story: t("getInvolved.stories.story3"),
      location: "Malakal, South Sudan",
      avatar: "/images/volunteer-2.jpg",
    },
    {
      name: "Akec Garang",
      role: t("getInvolved.stories.donor"),
      story: t("getInvolved.stories.story4"),
      location: "Bor, South Sudan",
      avatar: "/images/donor-2.jpg",
    },
  ];

  const contactInfo = [
    {
      icon: <Phone className="h-6 w-6" />,
      title: t("getInvolved.contact.call"),
      details: "+211 912 446 7697",
      description: t("getInvolved.contact.callDesc"),
    },
    {
      icon: <Phone className="h-6 w-6" />,
      title: t("getInvolved.contact.altPhone"),
      details: "+211 981 593 738",
      description: t("getInvolved.contact.altPhoneDesc"),
    },
    {
      icon: <Mail className="h-6 w-6" />,
      title: t("getInvolved.contact.primaryEmail"),
      details: "infoacdha@gmail.com",
      description: t("getInvolved.contact.primaryEmailDesc"),
    },
    {
      icon: <Mail className="h-6 w-6" />,
      title: t("getInvolved.contact.secondaryEmail"),
      details: "info@acdha.org",
      description: t("getInvolved.contact.secondaryEmailDesc"),
    },
    {
      icon: <MapPin className="h-6 w-6" />,
      title: t("getInvolved.contact.visit"),
      details: "Hai, Malakia Police Building",
      description: t("getInvolved.contact.visitDesc"),
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-white">
      {/* Hero Section */}
      <section className="relative min-h-[50vh] sm:min-h-[60vh] md:min-h-[70vh] flex items-center justify-center overflow-hidden bg-cover bg-center md:bg-fixed">
        {/* Background Image with Overlay */}
        <div
          className=""
          style={{
            backgroundImage: `linear-gradient(135deg, rgba(22, 101, 52, 0.85), rgba(21, 128, 61, 0.75), rgba(34, 197, 94, 0.3)), url('/images/bg_5.jpg')`,
          }}
        >
          {/* Pattern Overlay */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2260%22%20height%3D%2260%22%20viewBox%3D%220%200%2060%2060%22%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%3E%3Cg%20fill%3D%22none%22%20fill-rule%3D%22evenodd%22%3E%3Cg%20fill%3D%22%23ffffff%22%20fill-opacity%3D%220.1%22%3E%3Ccircle%20cx%3D%2230%22%20cy%3D%2230%22%20r%3D%222%22/%3E%3C/g%3E%3C/g%3E%3C/svg%3E')]"></div>
          </div>

          {/* Content Container */}
          <div className="relative z-10 mx-auto w-full px-4 py-12 sm:py-16 md:py-24 lg:py-32 text-center">
            <div className="mb-4 sm:mb-6 inline-flex items-center rounded-full bg-yellow-500/20 px-3 py-1.5 sm:px-4 sm:py-2 backdrop-blur-sm border border-yellow-400/30">
              <span className="mr-1.5 sm:mr-2 h-1.5 w-1.5 sm:h-2 sm:w-2 rounded-full bg-yellow-400 animate-pulse"></span>
              <span className="text-xs sm:text-sm font-semibold text-yellow-300">
                Make a Difference Today
              </span>
            </div>

            <h1 className="mb-4 sm:mb-6 text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white leading-tight">
              <span className="block mb-1 sm:mb-2">
                {t("getInvolved.hero.title")}
              </span>
              <span className="block bg-gradient-to-r from-yellow-300 via-yellow-200 to-yellow-300 bg-clip-text text-transparent">
                {t("getInvolved.hero.subtitle")}
              </span>
            </h1>

            <p className="mx-auto mb-6 sm:mb-8 md:mb-10 max-w-3xl text-base sm:text-lg md:text-xl text-green-100 leading-relaxed px-2">
              {t("getInvolved.hero.description")}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a
                href="#involvement-options"
                className="group inline-flex items-center justify-center space-x-2 rounded-full bg-gradient-to-r from-yellow-500 to-yellow-400 px-8 py-4 text-lg font-bold text-green-900 shadow-xl transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-yellow-500/50"
              >
                <span>Get Started</span>
                <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
              </a>
              <a
                href="#contact"
                className="inline-flex items-center justify-center rounded-full border-2 border-white/50 bg-white/10 px-8 py-4 text-lg font-semibold text-white backdrop-blur-sm transition-all duration-300 hover:bg-white/20 hover:border-white"
              >
                Contact Us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Impact Stats */}
      <section className="bg-white px-4 py-20 md:px-8 lg:px-16">
        <div className="mx-auto max-w-7xl">
          <div className="mb-16 text-center">
            <h2 className="mb-6 text-4xl font-bold text-green-800 md:text-5xl">
              {t("getInvolved.impact.title")}
            </h2>
            <div className="mx-auto h-1 w-24 bg-green-600"></div>
          </div>

          <div className="grid grid-cols-2 gap-8 md:grid-cols-4">
            {impactStats.map((stat, index) => (
              <div key={stat.label} className="text-center">
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
                  <div className="text-green-700">{stat.icon}</div>
                </div>
                <div className="mb-2 text-3xl font-bold text-green-800 md:text-4xl">
                  {stat.number}
                </div>
                <div className="font-medium text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Involvement Options */}
      <section
        id="involvement-options"
        className="bg-gradient-to-br from-green-50 to-white px-4 py-20 md:px-8 lg:px-16"
      >
        <div className="mx-auto max-w-7xl">
          <div className="mb-16 text-center">
            <h2 className="mb-6 text-4xl font-bold text-green-800 md:text-5xl">
              {t("getInvolved.ways.title")}
            </h2>
            <div className="mx-auto mb-6 h-1 w-24 bg-green-600"></div>
            <p className="mx-auto max-w-3xl text-xl text-gray-700">
              {t("getInvolved.ways.subtitle")}
            </p>
          </div>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
            {involvementOptions.map((option, index) => (
              <div
                key={option.title}
                className="group overflow-hidden rounded-2xl bg-white shadow-lg transition-all duration-300 hover:shadow-2xl"
              >
                <div className={`h-2 bg-gradient-to-r ${option.color}`}></div>
                <div className="p-8">
                  <div
                    className={`h-16 w-16 ${option.bgColor} mb-6 flex items-center justify-center rounded-full`}
                  >
                    <div className={option.iconColor}>{option.icon}</div>
                  </div>
                  <h3 className="mb-4 text-2xl font-bold text-green-800">
                    {option.title}
                  </h3>
                  <p className="mb-6 leading-relaxed text-gray-600">
                    {option.description}
                  </p>

                  <div className="mb-6">
                    <h4 className="mb-3 font-semibold text-green-800">
                      {t("getInvolved.whatYouGet")}
                    </h4>
                    <ul className="space-y-2">
                      {option.features.map((feature) => (
                        <li
                          key={feature}
                          className="flex items-center text-gray-600"
                        >
                          <CheckCircle className="mr-2 h-4 w-4 flex-shrink-0 text-green-600" />
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <a
                    href={option.buttonLink}
                    className="inline-flex w-full items-center justify-center rounded-lg bg-green-800 px-6 py-3 font-semibold text-white transition-colors duration-300 group-hover:shadow-lg hover:bg-green-700"
                  >
                    {option.buttonText}
                    <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="bg-white px-4 py-20 md:px-8 lg:px-16">
        <div className="mx-auto max-w-7xl">
          <div className="mb-16 text-center">
            <h2 className="mb-6 text-4xl font-bold text-green-800 md:text-5xl">
              {t("getInvolved.stories.title")}
            </h2>
            <div className="mx-auto mb-6 h-1 w-24 bg-green-600"></div>
            <p className="mx-auto max-w-3xl text-xl text-gray-700">
              {t("getInvolved.stories.subtitle")}
            </p>
          </div>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
            {volunteerDonorStories.map((story, index) => (
              <div
                key={story.name}
                className="rounded-2xl border-l-4 border-green-600 bg-gradient-to-br from-green-50 to-green-100 p-8"
              >
                <div className="flex items-start space-x-4">
                  <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-green-600">
                    <span className="font-semibold text-white">
                      {story.name.charAt(0)}
                    </span>
                  </div>
                  <div className="flex-1">
                    <p className="mb-4 leading-relaxed text-gray-700 italic">
                      "{story.story}"
                    </p>
                    <div className="flex items-center justify-between">
                      <div>
                        <h4 className="font-semibold text-green-800">
                          {story.name}
                        </h4>
                        <p className="text-sm text-gray-600">
                          {story.role} • {story.location}
                        </p>
                      </div>
                      <div className="flex space-x-1">
                        {[...Array(5)].map((_, i) => (
                          <Star
                            key={i}
                            className="h-4 w-4 fill-current text-yellow-400"
                          />
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="bg-gradient-to-br from-green-50 to-white px-4 py-20 md:px-8 lg:px-16">
        <div className="mx-auto max-w-4xl">
          <div className="mb-16 text-center">
            <h2 className="mb-6 text-4xl font-bold text-green-800 md:text-5xl">
              {t("getInvolved.faqs.title")}
            </h2>
            <div className="mx-auto mb-6 h-1 w-24 bg-green-600"></div>
            <p className="text-xl text-gray-700">
              {t("getInvolved.faqs.subtitle")}
            </p>
          </div>

          <div className="space-y-6">
            {faqs.map((faq, index) => (
              <div
                key={faq.question}
                className="rounded-xl border-l-4 border-green-600 bg-white p-8 shadow-lg"
              >
                <h3 className="mb-4 text-xl font-bold text-green-800">
                  {faq.question}
                </h3>
                <p className="leading-relaxed text-gray-700">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="bg-white px-4 py-20 md:px-8 lg:px-16">
        <div className="mx-auto max-w-7xl">
          <div className="mb-16 text-center">
            <h2 className="mb-6 text-4xl font-bold text-green-800 md:text-5xl">
              {t("getInvolved.getInTouch.title")}
            </h2>
            <div className="mx-auto mb-6 h-1 w-24 bg-green-600"></div>
            <p className="mx-auto max-w-3xl text-xl text-gray-700">
              {t("getInvolved.getInTouch.subtitle")}
            </p>
          </div>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-5">
            {contactInfo.map((contact, index) => (
              <div
                key={contact.title}
                className="rounded-2xl bg-gradient-to-br from-green-50 to-green-100 p-8 text-center"
              >
                <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-green-600">
                  <div className="text-white">{contact.icon}</div>
                </div>
                <h3 className="mb-2 text-xl font-bold text-green-800">
                  {contact.title}
                </h3>
                <p className="mb-2 text-lg font-semibold text-gray-700">
                  {contact.details}
                </p>
                <p className="text-gray-600">{contact.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="bg-gradient-to-br from-green-800 to-green-700 px-4 py-20 md:px-8 lg:px-16">
        <div className="mx-auto max-w-4xl text-center">
          <div>
            <h2 className="mb-6 text-4xl font-bold text-white md:text-5xl">
              {t("getInvolved.cta.title")}
            </h2>
            <p className="mx-auto mb-8 max-w-2xl text-xl text-green-100">
              {t("getInvolved.cta.subtitle")}
            </p>
            <div className="flex flex-col justify-center gap-4 sm:flex-row">
              <a
                href="/volunteer"
                className="transform rounded-lg bg-yellow-500 px-8 py-4 text-lg font-semibold text-green-800 shadow-lg transition-all duration-300 hover:scale-105 hover:bg-yellow-400"
              >
                {t("getInvolved.cta.volunteer")}
              </a>
              <a
                href="/donate"
                className="transform rounded-lg border-2 border-white bg-transparent px-8 py-4 text-lg font-semibold text-white transition-all duration-300 hover:scale-105 hover:bg-white/10"
              >
                {t("getInvolved.cta.donate")}
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
